import React from 'react';
import type { Material, MaterialFolder, SupplierCatalog } from '../../types/material';
import { MaterialSearchToolbar } from './MaterialSearchToolbar';
import { MaterialMobileCardList } from './MaterialMobileCardList';
import { MaterialDesktopTable } from './MaterialDesktopTable';
import { MaterialSearchPagination } from './MaterialSearchPagination';

export type MaterialienListPanelProps = {
  listRef: React.RefObject<HTMLDivElement | null>;
  ordnerFolders: MaterialFolder[];
  supplierCatalogs: SupplierCatalog[];
  selectedFolderId: string | null;
  selectedSupplierId: string | null;
  onClearFilters: () => void;
  materials: Material[];
  loading: boolean;
  emptyMessage: string;
  searchHint: string | null;
  searchResultsCount: number;
  searchMode: 'semantic' | 'keyword' | 'exact' | null;
  searchPage: number;
  searchTotalPages: number;
  searchPreference: 'semantic' | 'keyword';
  onSearchPreferenceChange: (mode: 'semantic' | 'keyword') => void;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSearchHintClear: () => void;
  onTriggerSearch: () => void;
  onCancelSearch: () => void;
  searchSubmitting: boolean;
  editingId: string | null;
  editDraft: Partial<Material>;
  setEditDraft: React.Dispatch<React.SetStateAction<Partial<Material>>>;
  saving: boolean;
  onStartEdit: (m: Material) => void;
  onCancelEdit: () => void;
  onSaveEdit: (m: Material, draft: Partial<Material>) => void;
  onDelete: (m: Material) => void;
  onOpenOmdPanel: (m: Material) => void;
  onEditMaterialMobile: (m: Material) => void;
  onSearchPageChange: (page: number) => void;
  omdEnabledSlugs: ReadonlySet<string>;
  onCreateClick: () => void;
};

export function MaterialienListPanel({
  listRef,
  ordnerFolders,
  supplierCatalogs,
  selectedFolderId,
  selectedSupplierId,
  onClearFilters,
  materials,
  loading,
  emptyMessage,
  searchHint,
  searchResultsCount,
  searchMode,
  searchPage,
  searchTotalPages,
  searchPreference,
  onSearchPreferenceChange,
  searchInput,
  onSearchInputChange,
  onSearchHintClear,
  onTriggerSearch,
  onCancelSearch,
  searchSubmitting,
  editingId,
  editDraft,
  setEditDraft,
  saving,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onOpenOmdPanel,
  onEditMaterialMobile,
  onSearchPageChange,
  omdEnabledSlugs,
  onCreateClick,
}: MaterialienListPanelProps) {
  const filteredEmpty = materials.length === 0 && !loading;
  const mobileEmptyMessage = filteredEmpty
    ? emptyMessage
    : 'Keine Materialien in dieser Ansicht (Ordner oder Suche anpassen).';

  return (
    <section
      ref={listRef}
      className="-mx-3 -mt-3 md:mx-0 md:mt-0 bg-app-surface border-0 overflow-hidden scroll-mt-4 md:flex md:min-h-0 md:flex-1 md:flex-col"
    >
      <MaterialSearchToolbar
        ordnerFolders={ordnerFolders}
        supplierCatalogs={supplierCatalogs}
        selectedFolderId={selectedFolderId}
        selectedSupplierId={selectedSupplierId}
        onClearFilters={onClearFilters}
        searchResultsCount={searchResultsCount}
        searchMode={searchMode}
        searchPreference={searchPreference}
        onSearchPreferenceChange={onSearchPreferenceChange}
        searchInput={searchInput}
        onSearchInputChange={onSearchInputChange}
        onSearchHintClear={onSearchHintClear}
        onTriggerSearch={onTriggerSearch}
        onCancelSearch={onCancelSearch}
        searchSubmitting={searchSubmitting}
        onCreateClick={onCreateClick}
      />
      {searchHint && <div className="px-4 pt-2 text-xs text-amber-700">{searchHint}</div>}
      <div className="md:hidden divide-y divide-app-border">
        <MaterialMobileCardList
          materials={materials}
          loading={loading}
          emptyMessage={mobileEmptyMessage}
          onOpenOmdPanel={onOpenOmdPanel}
          onEditMaterial={onEditMaterialMobile}
          omdEnabledSlugs={omdEnabledSlugs}
        />
      </div>
      <MaterialDesktopTable
        materials={materials}
        loading={loading}
        emptyMessage={emptyMessage}
        editingId={editingId}
        editDraft={editDraft}
        setEditDraft={setEditDraft}
        saving={saving}
        onStartEdit={onStartEdit}
        onCancelEdit={onCancelEdit}
        onSaveEdit={onSaveEdit}
        onDelete={onDelete}
        onOpenOmdPanel={onOpenOmdPanel}
        omdEnabledSlugs={omdEnabledSlugs}
      />
      {searchTotalPages > 1 && (
        <MaterialSearchPagination
          searchPage={searchPage}
          searchTotalPages={searchTotalPages}
          loading={loading}
          onPageChange={(nextPage) => {
            onSearchPageChange(nextPage);
            listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      )}
    </section>
  );
}
